import getConfig from 'next/config';
import { FetchClient } from '@cvent/fetch';
import { LoggerFactory } from '@cvent/logging/LoggerFactory';
import { UserType } from '@cvent/universal-login-web-graphql/types';

const LOG = LoggerFactory.create('OktaResolver');
const { publicRuntimeConfig } = getConfig();

export type SessionResponse = {
  success: boolean;
  id?: string;
};

/**
 * Cookies are passed through by configuring the credentials property.
 * This is needed on both Okta calls as we want to forward user tied
 * Okta information which is stored in the cookie.
 *
 * Ref: https://developer.mozilla.org/en-US/docs/Web/API/fetch#parameters
 */
export class SessionManager {
  readonly client: FetchClient;
  readonly baseUrl: string;

  private readonly failureResponse: SessionResponse;

  constructor() {
    this.client = new FetchClient({
      init: {
        headers: {
          accept: 'application/json'
        },
        credentials: 'include'
      }
    });
    this.baseUrl = publicRuntimeConfig.OKTA_URL;
    this.failureResponse = { success: false };
  }

  private oktaUrl(path: string) {
    return `${this.baseUrl}${path}`;
  }

  /**
   * https://developer.okta.com/docs/reference/api/sessions/#get-session
   */
  public async getSession(): Promise<SessionResponse> {
    try {
      const res = await this.client.get(this.oktaUrl('/api/v1/sessions/me'));
      const resBody = await res.json();

      return { success: res.ok, id: resBody.id };
    } catch (error) {
      LOG.info('Unable to find existing okta session.', error);

      return this.failureResponse;
    }
  }

  /**
   * https://developer.okta.com/docs/reference/api/sessions/#close-session
   */
  public async closeSession(userType?: string): Promise<SessionResponse> {
    /**
     * No-Op if user is REAL.
     */
    if (userType && userType === UserType.Real) return this.failureResponse;

    try {
      const res = await this.client.delete(this.oktaUrl('/api/v1/sessions/me'), null);

      return { success: res.ok };
    } catch (error) {
      LOG.info('No session found to close.', error);

      return this.failureResponse;
    }
  }
}
